import { NO, UIColor, UITextView, UITimer, UIView, YES } from "uicore-ts"
import { RHColor } from "./RHColor"


export type RHTransientBannerHorizontalPosition = "left" | "center" | "right"
export type RHTransientBannerVerticalPosition = "top" | "center" | "bottom"


export interface RHTransientBannerOptions {
    message: string
    horizontalPosition?: RHTransientBannerHorizontalPosition
    verticalPosition?: RHTransientBannerVerticalPosition
    /** Seconds before the banner fades out after appearing (or after hover ends). Defaults to 2. */
    fadeOutDelay?: number
    backgroundColor?: UIColor
    textColor?: UIColor
}


/**
 * RHTransientBanner
 *
 * A lightweight floating message banner that fades in, stays visible while
 * hovered, and fades out after `fadeOutDelay` seconds.
 *
 * Multiple banners sharing the same container and position slot are stacked
 * as rows and reflow when any one of them is dismissed.
 *
 * Position is expressed as two independent variant strings:
 *   horizontalPosition: "left" | "center" | "right"
 *   verticalPosition:   "top"  | "center" | "bottom"
 *
 * Usage — static factory (most common):
 *   RHTransientBanner.show(containerView, { message: "Layout debugger ON" })
 *
 * Usage — manual:
 *   const banner = new RHTransientBanner()
 *   banner.configure({ message: "Hello", verticalPosition: "bottom" })
 *   banner.present(containerView)
 */
export class RHTransientBanner extends UIView {
    
    static readonly _fadeInDuration = 0.18   // seconds
    static readonly _fadeOutDuration = 0.35  // seconds
    static readonly _paddingVertical = 10
    static readonly _paddingHorizontal = 16
    static readonly _stackGap = 8
    static readonly _edgeMargin = 24
    
    /**
     * Tracks all live banners grouped by container view.
     * Banners in the same container AND same position slot are stacked.
     */
    static _liveBanners: Map<UIView, RHTransientBanner[]> = new Map()
    
    readonly messageLabel = new UITextView().configuredWithObject({
        fontSize: 12,
        isSingleLine: NO,
        style: {
            fontWeight: "600",
            lineHeight: "1.4",
            userSelect: "none",
            pointerEvents: "none"
        }
    })
    
    _horizontalPosition: RHTransientBannerHorizontalPosition = "center"
    _verticalPosition: RHTransientBannerVerticalPosition = "bottom"
    _fadeOutDelay: number = 2
    _dismissTimer?: UITimer
    _isHovered: boolean = false
    _containerView?: UIView
    // Cached banner dimensions, set in present() and used for stack reflow
    _bannerWidth: number = 0
    _bannerHeight: number = 0
    
    
    constructor() {
        
        super()
        
        ;(this as RHTransientBanner).configureWithObject({
            userInteractionEnabled: true,
            style: {
                opacity: "0",
                transition: `opacity ${RHTransientBanner._fadeInDuration}s ease`,
                pointerEvents: "none",
                zIndex: "9999",
                position: "absolute",
                borderRadius: "8px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                boxSizing: "border-box"
            }
        })
        
        this.messageLabel.addedAsSubviewToView(this)
        
        // noinspection JSConstantReassignment
        this.controlEventTargetAccumulator.PointerEnter = () => {
            this._didPointerEnter()
        }
        
        // noinspection JSConstantReassignment
        this.controlEventTargetAccumulator.PointerLeave = () => {
            this._didPointerLeave()
        }
        
    }
    
    
    // -------------------------------------------------------------------------
    // Public API
    // -------------------------------------------------------------------------
    
    configure(options: RHTransientBannerOptions) {
        
        this._horizontalPosition = options.horizontalPosition ?? "center"
        this._verticalPosition = options.verticalPosition ?? "bottom"
        this._fadeOutDelay = options.fadeOutDelay ?? 2
        
        const backgroundColor = options.backgroundColor ?? RHColor.dark.colorWithAlpha(0.88)
        const textColor = options.textColor ?? UIColor.whiteColor
        
        this.messageLabel.configureWithObject({
            text: options.message,
            textColor: textColor
        })
        
        this.backgroundColor = backgroundColor
        
    }
    
    
    /**
     * Adds the banner to `containerView`, registers it in the live stack,
     * fades it in, and starts the dismiss timer.
     */
    present(containerView: UIView) {
        
        this._containerView = containerView
        
        const pv = RHTransientBanner._paddingVertical
        const ph = RHTransientBanner._paddingHorizontal
        const maxBannerWidth = 360
        const bannerWidth = Math.min(maxBannerWidth, containerView.bounds.width - 32)
        const labelWidth = bannerWidth - ph * 2
        const contentHeight = this.messageLabel.intrinsicContentHeight(labelWidth)
        const bannerHeight = contentHeight + pv * 2
        
        this._bannerWidth = bannerWidth
        this._bannerHeight = bannerHeight
        
        // Register before adding to the view tree so the stack is correct
        // when we compute positions below
        RHTransientBanner._registerBanner(this, containerView)
        
        this.addedAsSubviewToView(containerView)
        
        // Reflow the whole slot — this positions us and shifts any existing banners
        RHTransientBanner._repositionSlot(this._horizontalPosition, this._verticalPosition, containerView)
        
        // Enable pointer events now that we are positioned
        this.setStyleProperty("pointerEvents", "auto")
        
        // Force a reflow so the initial opacity: 0 is painted before we
        // transition to 1 (otherwise the browser skips the transition).
        void this.viewHTMLElement.offsetWidth
        
        this.setStyleProperty("transition", `opacity ${RHTransientBanner._fadeInDuration}s ease`)
        this.setStyleProperty("opacity", "1")
        
        this._startDismissTimer()
        
    }
    
    
    /**
     * Fades the banner out, removes it from the stack, and triggers an
     * animated reflow of the remaining banners in its slot.
     */
    dismiss() {
        
        this._cancelDismissTimer()
        
        this.setStyleProperty("transition", `opacity ${RHTransientBanner._fadeOutDuration}s ease`)
        this.setStyleProperty("opacity", "0")
        
        setTimeout(() => {
            
            const containerView = this._containerView
            const horizontalPosition = this._horizontalPosition
            const verticalPosition = this._verticalPosition
            
            this.removeFromSuperview()
            RHTransientBanner._unregisterBanner(this)
            
            if (containerView !== undefined) {
                RHTransientBanner._repositionSlot(horizontalPosition, verticalPosition, containerView, YES)
            }
            
        }, RHTransientBanner._fadeOutDuration * 1000 + 50)
        
    }
    
    
    // -------------------------------------------------------------------------
    // Static factory
    // -------------------------------------------------------------------------
    
    /**
     * Creates, configures, and presents a banner in one call.
     * Returns the banner instance in case the caller wants to dismiss it early.
     */
    static show(containerView: UIView, options: RHTransientBannerOptions): RHTransientBanner {
        
        const banner = new RHTransientBanner()
        banner.configure(options)
        banner.present(containerView)
        return banner
        
    }
    
    
    // -------------------------------------------------------------------------
    // Static stack management
    // -------------------------------------------------------------------------
    
    private static _registerBanner(banner: RHTransientBanner, containerView: UIView) {
        
        const existing = RHTransientBanner._liveBanners.get(containerView) ?? []
        existing.push(banner)
        RHTransientBanner._liveBanners.set(containerView, existing)
        
    }
    
    
    private static _unregisterBanner(banner: RHTransientBanner) {
        
        if (banner._containerView === undefined) {
            return
        }
        
        const existing = RHTransientBanner._liveBanners.get(banner._containerView)
        
        if (existing === undefined) {
            return
        }
        
        const index = existing.indexOf(banner)
        
        if (index !== -1) {
            existing.splice(index, 1)
        }
        
        if (existing.length === 0) {
            RHTransientBanner._liveBanners.delete(banner._containerView)
        }
        
    }
    
    
    /**
     * Recomputes and applies positions for all live banners in the given slot.
     * When `animated` is true a CSS position transition is added so remaining
     * banners slide into their new positions as a dismissed one leaves.
     */
    private static _repositionSlot(
        horizontalPosition: RHTransientBannerHorizontalPosition,
        verticalPosition: RHTransientBannerVerticalPosition,
        containerView: UIView,
        animated: boolean = NO
    ) {
        
        const allInContainer = RHTransientBanner._liveBanners.get(containerView) ?? []
        const slotBanners = allInContainer.filter(
            b => b._horizontalPosition === horizontalPosition && b._verticalPosition === verticalPosition
        )
        
        if (slotBanners.length === 0) {
            return
        }
        
        const containerWidth = containerView.bounds.width
        const containerHeight = containerView.bounds.height
        const margin = RHTransientBanner._edgeMargin
        const gap = RHTransientBanner._stackGap
        
        slotBanners.forEach((banner, index) => {
            
            const x = RHTransientBanner._resolveX(horizontalPosition, containerWidth, banner._bannerWidth, margin)
            const y = RHTransientBanner._resolveY(
                verticalPosition,
                containerHeight,
                margin,
                gap,
                index,
                slotBanners
            )
            
            const positionTransition = animated
                                       ? `, top ${RHTransientBanner._fadeOutDuration}s ease, left ${RHTransientBanner._fadeOutDuration}s ease`
                                       : ""
            
            banner.setStyleProperty(
                "transition",
                `opacity ${RHTransientBanner._fadeInDuration}s ease${positionTransition}`
            )
            
            banner.setPosition(x, undefined, undefined, y, banner._bannerHeight, banner._bannerWidth)
            
        })
        
    }
    
    
    private static _resolveX(
        horizontalPosition: RHTransientBannerHorizontalPosition,
        containerWidth: number,
        bannerWidth: number,
        margin: number
    ): number {
        
        if (horizontalPosition === "left") {
            return margin
        }
        
        if (horizontalPosition === "right") {
            return containerWidth - bannerWidth - margin
        }
        
        // center
        return (containerWidth - bannerWidth) / 2
        
    }
    
    
    private static _resolveY(
        verticalPosition: RHTransientBannerVerticalPosition,
        containerHeight: number,
        margin: number,
        gap: number,
        index: number,
        slotBanners: RHTransientBanner[]
    ): number {
        
        if (verticalPosition === "bottom") {
            
            // Stack grows upward: the last (newest) banner sits at the bottom
            // edge; older banners are above it.
            let y = containerHeight - margin
            
            for (let i = slotBanners.length - 1; i >= index; i--) {
                y -= slotBanners[i]._bannerHeight
                if (i > index) {
                    y -= gap
                }
            }
            
            return y
            
        }
        
        if (verticalPosition === "top") {
            
            // Stack grows downward: the last (newest) banner sits at the top
            // edge; older banners are below it.
            let y = margin
            
            for (let i = slotBanners.length - 1; i > index; i--) {
                y += slotBanners[i]._bannerHeight + gap
            }
            
            return y
            
        }
        
        // center: stack centered in the container
        const totalHeight = slotBanners.reduce((sum, b) => sum + b._bannerHeight, 0)
            + gap * (slotBanners.length - 1)
        let y = (containerHeight - totalHeight) / 2
        
        for (let i = 0; i < index; i++) {
            y += slotBanners[i]._bannerHeight + gap
        }
        
        return y
        
    }
    
    
    // -------------------------------------------------------------------------
    // Private instance helpers
    // -------------------------------------------------------------------------
    
    private _startDismissTimer() {
        
        this._cancelDismissTimer()
        
        this._dismissTimer = new UITimer(this._fadeOutDelay, false, () => {
            if (!this._isHovered) {
                this.dismiss()
            }
        })
        
    }
    
    
    private _cancelDismissTimer() {
        
        this._dismissTimer?.invalidate()
        this._dismissTimer = undefined
        
    }
    
    
    private _didPointerEnter() {
        
        this._isHovered = true
        this._cancelDismissTimer()
        
    }
    
    
    private _didPointerLeave() {
        
        this._isHovered = false
        this._startDismissTimer()
        
    }
    
    
    override layoutSubviews() {
        
        super.layoutSubviews()
        
        const pv = RHTransientBanner._paddingVertical
        const ph = RHTransientBanner._paddingHorizontal
        this.messageLabel.frame = this.contentBoundsWithInsets(ph, ph, pv, pv)
        
    }
    
}
